import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  officeGetQuote, officeSendQuote, officeAcceptQuote, officeConvertQuoteToOrder,
} from "@/lib/office.functions";
import {
  listCustomerDocuments,
  getCustomerDocumentUrl,
  officeUploadCustomerDocument,
  officeDeleteCustomerDocument,
  officeGenerateWorkOrderBundle,
  officePreviewBundle,
  officePreviewBundlePdf,
} from "@/lib/customer-documents.functions";
import { officeGetWorkOrder, officeSaveWorkOrder } from "@/lib/work-orders.functions";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Send, CheckCircle2, Package, Download, FileText,
  Clock, Mail, CreditCard, AlertTriangle, CheckCircle, FilePlus, PenLine, Webhook,
  FolderOpen, Image as ImageIcon, Upload, Trash2, Loader2, Eye, ChevronUp, ChevronDown, X,
} from "lucide-react";
import { PAYMENT_PLANS } from "@/lib/payment-plans";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";

export const Route = createFileRoute("/_employee/medewerker/office/offertes/$id")({
  head: () => ({ meta: [{ title: "Offerte — EVG Office" }, { name: "robots", content: "noindex" }] }),
  component: QuoteDetail,
});

function fmt(c: number) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);
}

function QuoteDetail() {
  const { id } = useParams({ from: "/_employee/medewerker/office/offertes/$id" });
  const get = useServerFn(officeGetQuote);
  const sendFn = useServerFn(officeSendQuote);
  const acceptFn = useServerFn(officeAcceptQuote);
  const convertFn = useServerFn(officeConvertQuoteToOrder);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ["office-quote", id], queryFn: () => get({ data: { id } }) });
  const [scheduleNow, setScheduleNow] = useState(true);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const sendMut = useMutation({ mutationFn: () => sendFn({ data: { id } }), onSuccess: () => { toast.success("Verzonden"); qc.invalidateQueries({ queryKey: ["office-quote", id] }); } });
  const acceptMut = useMutation({ mutationFn: () => acceptFn({ data: { quote_id: id } }), onSuccess: () => { toast.success("Getekend — deelfacturen aangemaakt"); qc.invalidateQueries({ queryKey: ["office-quote", id] }); } });
  const convertMut = useMutation({ mutationFn: () => convertFn({ data: { quote_id: id, schedule_now: scheduleNow } }), onSuccess: (r) => { toast.success("Order aangemaakt"); qc.invalidateQueries({ queryKey: ["office-quote", id] }); } });

  if (isLoading || !data) return <div className="p-8 text-center text-slate-400">Laden…</div>;
  const q = data.quote;
  const inv = data.invoices;
  const webhooks = ((data as any).webhooks ?? []) as Array<{
    id: string; mollie_payment_id: string; status: string | null; outcome: string | null;
    note: string | null; received_at: string; processed_at: string | null;
  }>;
  const plan = PAYMENT_PLANS[(q.payment_plan ?? "30-50-20") as keyof typeof PAYMENT_PLANS];

  // Build per-phase timeline events
  type Ev = { at: string; kind: "created" | "scheduled" | "sent" | "error" | "webhook" | "paid"; label: string; detail?: string };
  const phaseTimelines = inv.map((i: any) => {
    const events: Ev[] = [];
    if (i.created_at) events.push({ at: i.created_at, kind: "created", label: "Deelfactuur aangemaakt" });
    if (i.send_at) events.push({ at: i.send_at, kind: "scheduled", label: "Verzending gepland" });
    if (i.sent_at) events.push({ at: i.sent_at, kind: "sent", label: "E-mail verzonden naar klant" });
    if (i.last_send_error && !i.sent_at) events.push({ at: i.updated_at ?? i.created_at, kind: "error", label: "Verzendfout", detail: i.last_send_error });
    for (const w of webhooks) {
      if (w.mollie_payment_id !== i.mollie_payment_id) continue;
      events.push({
        at: w.received_at,
        kind: "webhook",
        label: `Mollie webhook · ${w.status ?? "?"}`,
        detail: [w.outcome, w.note].filter(Boolean).join(" — ") || undefined,
      });
    }
    if (i.paid_at) events.push({ at: i.paid_at, kind: "paid", label: "Betaling ontvangen" });
    events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    return { invoice: i, events };
  });

  const quoteEvents: Ev[] = [];
  if (q.created_at) quoteEvents.push({ at: q.created_at, kind: "created", label: "Offerte aangemaakt" });
  if (q.sent_at) quoteEvents.push({ at: q.sent_at, kind: "sent", label: "Offerte verzonden" });
  if (q.accepted_at) quoteEvents.push({ at: q.accepted_at, kind: "paid", label: "Offerte geaccepteerd" });
  if (q.signed_at) quoteEvents.push({ at: q.signed_at, kind: "paid", label: `Ondertekend${q.signed_name ? ` door ${q.signed_name}` : ""}` });

  const evIcon = (k: Ev["kind"]) => {
    const cls = "size-3.5";
    if (k === "created") return <FilePlus className={cls} />;
    if (k === "scheduled") return <Clock className={cls} />;
    if (k === "sent") return <Mail className={cls} />;
    if (k === "error") return <AlertTriangle className={cls} />;
    if (k === "webhook") return <Webhook className={cls} />;
    if (k === "paid") return <CheckCircle className={cls} />;
    return <PenLine className={cls} />;
  };
  const evColor = (k: Ev["kind"]) =>
    k === "error" ? "bg-rose-100 text-rose-700 ring-rose-200"
    : k === "paid" ? "bg-emerald-100 text-emerald-700 ring-emerald-200"
    : k === "sent" ? "bg-sky-100 text-sky-700 ring-sky-200"
    : k === "webhook" ? "bg-violet-100 text-violet-700 ring-violet-200"
    : k === "scheduled" ? "bg-amber-100 text-amber-700 ring-amber-200"
    : "bg-slate-100 text-slate-600 ring-slate-200";
  const fmtDT = (s: string) => new Date(s).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });

  return (
    <div className="space-y-5">
      <Link to="/medewerker/office/offertes" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0b2a4a]"><ArrowLeft className="size-3" /> Terug</Link>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs text-slate-500">{q.quote_number}</p>
                <h1 className="text-2xl font-bold text-slate-900">{q.title}</h1>
                <p className="mt-1 text-sm text-slate-500">Status: <span className="font-semibold">{q.status}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPdfPreviewOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold hover:bg-slate-200"
                >
                  <Eye className="size-3.5" /> Voorbeeld
                </button>
                <a href={`/api/public/quotes/pdf?id=${q.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold hover:bg-slate-200">
                  <Download className="size-3.5" /> PDF
                </a>
              </div>
            </div>

            <table className="mt-5 w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr><th className="text-left p-2">Omschrijving</th><th className="text-right p-2">Aantal</th><th className="text-right p-2">Prijs</th><th className="text-right p-2">Regel</th></tr>
              </thead>
              <tbody>
                {(q.line_items as any[]).map((it: any, i: number) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-2">{it.description}</td>
                    <td className="p-2 text-right">{it.qty}</td>
                    <td className="p-2 text-right">{fmt(it.unit_price_cents)}</td>
                    <td className="p-2 text-right">{fmt(Math.round(it.qty * it.unit_price_cents * (1 - (it.discount_pct ?? 0) / 100)))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <dl className="mt-4 ml-auto max-w-xs space-y-1 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Subtotaal</dt><dd>{fmt(q.subtotal_cents)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">BTW</dt><dd>{fmt(q.vat_cents)}</dd></div>
              <div className="flex justify-between border-t pt-1 font-semibold"><dt>Totaal</dt><dd>{fmt(q.total_cents)}</dd></div>
            </dl>
          </div>

          <WorkOrderCard quoteId={q.id} quoteNumber={q.quote_number} hasInvoices={inv.length > 0} />

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">

            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><FileText className="size-4" /> Factuurstatus per fase ({plan.label})</h2>
            {inv.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">Nog geen deelfacturen — worden aangemaakt zodra de offerte is geaccepteerd.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {inv.map((i: any) => {
                  const now = Date.now();
                  const sendAt = i.send_at ? new Date(i.send_at) : null;
                  const sentAt = i.sent_at ? new Date(i.sent_at) : null;
                  const dueAt = i.due_at ? new Date(i.due_at) : null;
                  const paid = i.status === "paid";
                  const hasError = !!i.last_send_error;
                  const overdue = !paid && dueAt && dueAt.getTime() < now;
                  const scheduled = !sentAt && sendAt && sendAt.getTime() > now;
                  const readyToSend = !sentAt && sendAt && sendAt.getTime() <= now;

                  const statusPill = paid
                    ? { label: "Betaald", cls: "bg-emerald-100 text-emerald-700" }
                    : hasError
                    ? { label: "Fout", cls: "bg-rose-100 text-rose-700" }
                    : overdue
                    ? { label: "Achterstallig", cls: "bg-rose-100 text-rose-700" }
                    : sentAt
                    ? { label: "Verzonden — open", cls: "bg-amber-100 text-amber-700" }
                    : scheduled
                    ? { label: "Gepland", cls: "bg-sky-100 text-sky-700" }
                    : readyToSend
                    ? { label: "In wachtrij", cls: "bg-slate-200 text-slate-700" }
                    : { label: i.status ?? "concept", cls: "bg-slate-100 text-slate-600" };

                  return (
                    <div key={i.id} className={`rounded-xl border p-4 text-sm ${hasError ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-slate-900">{i.phase} <span className="text-slate-400 font-normal">({i.pct}%)</span></div>
                          {i.invoice_number && <div className="font-mono text-[11px] text-slate-500">{i.invoice_number}</div>}
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-slate-900">{fmt(i.amount_cents)}</div>
                          <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusPill.cls}`}>{statusPill.label}</span>
                        </div>
                      </div>

                      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 sm:grid-cols-3">
                        <div>
                          <dt className="text-slate-400">Verzenden op</dt>
                          <dd>{sendAt ? sendAt.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-slate-400">Verzonden</dt>
                          <dd>{sentAt ? sentAt.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : "—"}</dd>
                        </div>
                        <div>
                          <dt className="text-slate-400">Vervaldatum</dt>
                          <dd className={overdue ? "text-rose-600 font-medium" : ""}>{dueAt ? dueAt.toLocaleDateString("nl-NL") : "—"}</dd>
                        </div>
                        {i.paid_at && (
                          <div>
                            <dt className="text-slate-400">Betaald op</dt>
                            <dd>{new Date(i.paid_at).toLocaleDateString("nl-NL")}</dd>
                          </div>
                        )}
                        {i.mollie_payment_id && (
                          <div className="col-span-2">
                            <dt className="text-slate-400">Mollie ID</dt>
                            <dd className="font-mono text-[11px]">{i.mollie_payment_id}</dd>
                          </div>
                        )}
                      </dl>

                      {hasError && (
                        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                          <div className="font-semibold">Laatste verzendfout</div>
                          <div className="mt-0.5 break-words font-mono text-[11px]">{i.last_send_error}</div>
                        </div>
                      )}

                      {i.pay_url && !paid && (
                        <a href={i.pay_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-xs font-semibold text-[#0b2a4a] hover:underline">
                          Betaallink openen →
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Clock className="size-4" /> Tijdlijn</h2>
            <p className="mt-1 text-xs text-slate-500">Alle verzend- en betaalstatus-updates per fase, inclusief Mollie-webhook-events.</p>

            {quoteEvents.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Offerte</div>
                <ol className="mt-2 relative border-l border-slate-200 pl-5 space-y-2">
                  {quoteEvents.map((e, idx) => (
                    <li key={idx} className="relative">
                      <span className={`absolute -left-[27px] flex size-5 items-center justify-center rounded-full ring-2 ring-white ${evColor(e.kind)}`}>{evIcon(e.kind)}</span>
                      <div className="flex items-baseline justify-between gap-2 text-sm">
                        <div className="font-medium text-slate-800">{e.label}</div>
                        <div className="font-mono text-[11px] text-slate-500">{fmtDT(e.at)}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {phaseTimelines.map(({ invoice: i, events }) => (
              <div key={i.id} className="mt-5">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <span>Fase — {i.phase} ({i.pct}%)</span>
                  <span className="font-mono text-slate-400">{fmt(i.amount_cents)}</span>
                </div>
                {events.length === 0 ? (
                  <p className="mt-2 text-xs text-slate-400 italic">Nog geen updates.</p>
                ) : (
                  <ol className="mt-2 relative border-l border-slate-200 pl-5 space-y-2">
                    {events.map((e, idx) => (
                      <li key={idx} className="relative">
                        <span className={`absolute -left-[27px] flex size-5 items-center justify-center rounded-full ring-2 ring-white ${evColor(e.kind)}`}>{evIcon(e.kind)}</span>
                        <div className="flex items-baseline justify-between gap-2 text-sm">
                          <div className="font-medium text-slate-800">{e.label}</div>
                          <div className="font-mono text-[11px] text-slate-500">{fmtDT(e.at)}</div>
                        </div>
                        {e.detail && (
                          <div className={`mt-0.5 break-words text-xs ${e.kind === "error" ? "text-rose-700" : "text-slate-500"}`}>{e.detail}</div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            ))}

            {phaseTimelines.length === 0 && (
              <p className="mt-4 text-sm text-slate-500">Nog geen deelfacturen — tijdlijn vult zich zodra de offerte is geaccepteerd.</p>
            )}
          </div>

          <DocumentsCard customerId={q.user_id} quoteId={q.id} quoteNumber={q.quote_number} />
        </div>

        <div className="space-y-3">
          {q.status === "draft" && (
            <Button className="w-full bg-[#0b2a4a] hover:bg-[#0e3463]" disabled={sendMut.isPending} onClick={() => sendMut.mutate()}>
              <Send className="size-4 mr-1" /> Verzenden naar klant
            </Button>
          )}
          {q.status !== "signed" && (
            <Button variant="outline" className="w-full" disabled={acceptMut.isPending} onClick={() => acceptMut.mutate()}>
              <CheckCircle2 className="size-4 mr-1" /> Handmatig accepteren
            </Button>
          )}
          {q.status === "signed" && (
            <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={scheduleNow} onChange={(e) => setScheduleNow(e.target.checked)} />
                Direct inplannen in agenda
              </label>
              <Button className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700" disabled={convertMut.isPending} onClick={() => convertMut.mutate()}>
                <Package className="size-4 mr-1" /> Omzetten naar order
              </Button>
            </div>
          )}
          <div className="rounded-2xl bg-slate-50 p-4 text-xs text-slate-500">
            <p className="font-semibold text-slate-700">{plan.label}</p>
            <p className="mt-1">{plan.description}</p>
          </div>
        </div>
      </div>
      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        title={`Offerte ${q.quote_number}`}
        description="Voorvertoning van de offerte-PDF"
        fileName={`Offerte-${q.quote_number}.pdf`}
        source={{ kind: "url", url: `/api/public/quotes/pdf?id=${q.id}` }}
      />
    </div>
  );
}

type WoItem = {
  description: string;
  qty: number;
  unit_price_cents: number;
  discount_pct?: number;
  sku?: string | null;
  product_id?: string | null;
};

function WorkOrderCard({
  quoteId,
  quoteNumber,
  hasInvoices,
}: {
  quoteId: string;
  quoteNumber: string;
  hasInvoices: boolean;
}) {
  const getFn = useServerFn(officeGetWorkOrder);
  const saveFn = useServerFn(officeSaveWorkOrder);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["office-work-order", quoteId],
    queryFn: () => getFn({ data: { quote_id: quoteId } }),
  });

  const [items, setItems] = useState<WoItem[] | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [discountPct, setDiscountPct] = useState<number | null>(null);

  const wo: any = data?.work_order;
  const rows: WoItem[] = items ?? ((wo?.line_items as WoItem[]) ?? []);
  const disc = discountPct ?? Number(wo?.discount_pct ?? 0);
  const noteVal = notes ?? (wo?.notes ?? "");

  const subtotal = rows.reduce(
    (s, it) =>
      s + Math.round((Number(it.qty) || 0) * (Number(it.unit_price_cents) || 0) * (1 - (Number(it.discount_pct) || 0) / 100)),
    0,
  );
  const afterGlobal = Math.round(subtotal * (1 - (Number(disc) || 0) / 100));
  const vat = Math.round(afterGlobal * 0.21);
  const total = afterGlobal + vat;

  const saveMut = useMutation({
    mutationFn: (finalize: boolean) =>
      saveFn({
        data: {
          quote_id: quoteId,
          line_items: rows.map((it) => ({
            description: it.description,
            qty: Number(it.qty) || 0,
            unit_price_cents: Math.round(Number(it.unit_price_cents) || 0),
            discount_pct: Number(it.discount_pct) || 0,
            sku: it.sku ?? null,
            product_id: it.product_id ?? null,
          })),
          discount_pct: Number(disc) || 0,
          notes: noteVal || null,
          finalize,
        },
      }),
    onSuccess: (res: any, finalize) => {
      setItems(null);
      setNotes(null);
      setDiscountPct(null);
      qc.invalidateQueries({ queryKey: ["office-work-order", quoteId] });
      qc.invalidateQueries({ queryKey: ["office-quote", quoteId] });
      if (finalize) {
        const up = res?.repriced?.updated ?? 0;
        toast.success(
          up > 0
            ? `Werkbon vastgesteld — ${up} openstaande factuur/facturen herberekend`
            : "Werkbon vastgesteld — facturen volgen deze aantallen",
        );
      } else {
        toast.success("Werkbon opgeslagen (concept)");
      }
    },
    onError: (e: any) => toast.error(e?.message ?? "Opslaan mislukt"),
  });

  const update = (i: number, patch: Partial<WoItem>) =>
    setItems(rows.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-white p-6 text-sm text-slate-400 shadow-sm ring-1 ring-slate-200">
        Werkbon laden…
      </div>
    );
  }

  const isFinal = wo?.status === "final";

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <PenLine className="size-4" /> Werkbon ({quoteNumber})
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Pas hier de werkelijk uitgevoerde aantallen aan. Zodra de werkbon is vastgesteld worden
            facturen op de werkbon gebaseerd — niet meer op de offerte.
          </p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            isFinal ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
          }`}
        >
          {isFinal ? "Vastgesteld" : "Concept"}
        </span>
      </div>

      <table className="mt-4 w-full text-sm">
        <thead className="text-xs uppercase text-slate-500">
          <tr>
            <th className="p-2 text-left">Omschrijving</th>
            <th className="p-2 text-right">Aantal</th>
            <th className="p-2 text-right">Prijs (€)</th>
            <th className="p-2 text-right">Korting %</th>
            <th className="p-2 text-right">Regel</th>
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="p-1.5">
                <input
                  aria-label={`Omschrijving regel ${i + 1}`}
                  className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm"
                  value={it.description}
                  onChange={(e) => update(i, { description: e.target.value })}
                />
              </td>
              <td className="p-1.5 text-right">
                <input
                  aria-label={`Aantal regel ${i + 1}`}
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right text-sm"
                  value={it.qty}
                  onChange={(e) => update(i, { qty: Number(e.target.value) })}
                />
              </td>
              <td className="p-1.5 text-right">
                <input
                  aria-label={`Prijs regel ${i + 1}`}
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-24 rounded-md border border-slate-200 px-2 py-1 text-right text-sm"
                  value={(it.unit_price_cents / 100).toFixed(2)}
                  onChange={(e) => update(i, { unit_price_cents: Math.round(Number(e.target.value) * 100) })}
                />
              </td>
              <td className="p-1.5 text-right">
                <input
                  aria-label={`Korting regel ${i + 1}`}
                  type="number"
                  min={0}
                  max={100}
                  className="w-16 rounded-md border border-slate-200 px-2 py-1 text-right text-sm"
                  value={it.discount_pct ?? 0}
                  onChange={(e) => update(i, { discount_pct: Number(e.target.value) })}
                />
              </td>
              <td className="p-2 text-right font-medium">
                {fmt(Math.round((Number(it.qty) || 0) * (Number(it.unit_price_cents) || 0) * (1 - (Number(it.discount_pct) || 0) / 100)))}
              </td>
              <td className="p-1.5 text-right">
                <button
                  type="button"
                  aria-label={`Regel ${i + 1} verwijderen`}
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                  onClick={() => setItems(rows.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="size-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold hover:bg-slate-200"
          onClick={() => setItems([...rows, { description: "Meerwerk", qty: 1, unit_price_cents: 0, discount_pct: 0 }])}
        >
          <FilePlus className="size-3.5" /> Regel toevoegen
        </button>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          Korting totaal (%)
          <input
            type="number"
            min={0}
            max={100}
            className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right text-sm"
            value={disc}
            onChange={(e) => setDiscountPct(Number(e.target.value))}
          />
        </label>
      </div>

      <textarea
        aria-label="Opmerkingen werkbon"
        className="mt-3 w-full rounded-lg border border-slate-200 p-2 text-sm"
        rows={2}
        placeholder="Opmerkingen monteur / meerwerk toelichting"
        value={noteVal}
        onChange={(e) => setNotes(e.target.value)}
      />

      <dl className="mt-4 ml-auto max-w-xs space-y-1 text-sm">
        <div className="flex justify-between"><dt className="text-slate-500">Subtotaal</dt><dd>{fmt(afterGlobal)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">BTW 21%</dt><dd>{fmt(vat)}</dd></div>
        <div className="flex justify-between border-t pt-1 font-semibold"><dt>Totaal</dt><dd>{fmt(total)}</dd></div>
      </dl>

      {hasInvoices && (
        <p className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
          Er bestaan al deelfacturen. Bij vaststellen worden alleen de nog niet betaalde facturen
          herberekend naar het werkbontotaal.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" disabled={saveMut.isPending} onClick={() => saveMut.mutate(false)}>
          Opslaan als concept
        </Button>
        <Button
          className="bg-emerald-600 hover:bg-emerald-700"
          disabled={saveMut.isPending || rows.length === 0}
          onClick={() => saveMut.mutate(true)}
        >
          <CheckCircle2 className="size-4 mr-1" /> Werkbon vaststellen &amp; factureren hierop
        </Button>
      </div>
    </div>
  );
}



const CATEGORY_LABELS: Record<string, string> = {
  situation_photo: "Situatiefoto",
  invoice: "Factuur",
  werkbon: "Werkbon",
  paklijst: "Paklijst",
  bundle: "Dossier (bundel)",
  other: "Overig",
};

function DocumentsCard({
  customerId,
  quoteId,
  quoteNumber,
}: {
  customerId: string;
  quoteId: string;
  quoteNumber: string;
}) {
  const listFn = useServerFn(listCustomerDocuments);
  const urlFn = useServerFn(getCustomerDocumentUrl);
  const uploadFn = useServerFn(officeUploadCustomerDocument);
  const deleteFn = useServerFn(officeDeleteCustomerDocument);
  const bundleFn = useServerFn(officeGenerateWorkOrderBundle);
  const previewFn = useServerFn(officePreviewBundle);
  const previewPdfFn = useServerFn(officePreviewBundlePdf);
  const qc = useQueryClient();
  const key = ["office-customer-documents", customerId];

  const q = useQuery({
    queryKey: key,
    queryFn: () => listFn({ data: { customerId } }),
  });

  const [category, setCategory] = useState<"situation_photo" | "other">("situation_photo");
  const [title, setTitle] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const openMut = useMutation({
    mutationFn: (id: string) => urlFn({ data: { id } }),
    onSuccess: (res) => {
      if (res?.url) window.open(res.url, "_blank", "noopener,noreferrer");
    },
  });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = "";
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(
          null,
          Array.from(bytes.subarray(i, i + chunk)),
        );
      }
      const base64 = btoa(binary);
      return uploadFn({
        data: {
          customer_id: customerId,
          quote_id: quoteId,
          category,
          title: title.trim() || file.name,
          mime_type: file.type || "application/octet-stream",
          filename: file.name,
          file_base64: base64,
        },
      });
    },
    onSuccess: () => {
      toast.success("Document toegevoegd aan klantaccount");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      invalidate();
    },
    onError: (e: any) => toast.error(e?.message ?? "Upload mislukt"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Verwijderd");
      invalidate();
    },
  });

  const bundleMut = useMutation({
    mutationFn: (sections: Array<{ kind: any; ref_id?: string | null }>) =>
      bundleFn({ data: { quote_id: quoteId, sections } }),
    onSuccess: () => {
      toast.success("Dossier gegenereerd — opgeslagen in klantaccount");
      setPreviewOpen(false);
      invalidate();
    },
    onError: (e: any) => toast.error(e?.message ?? "Kon dossier niet genereren"),
  });

  const documents = (q.data?.documents ?? []) as any[];
  const photos = documents.filter((d) => d.category === "situation_photo");
  const bundles = documents.filter((d) => d.category === "bundle");
  const others = documents.filter(
    (d) => d.category !== "situation_photo" && d.category !== "bundle",
  );

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <FolderOpen className="size-4" /> Documenten
        </h2>
        <span className="text-[11px] text-slate-500">
          Gekoppeld aan klantaccount — ook zichtbaar in "Mijn account → Documenten"
        </span>
      </div>

      <div className="mt-4 grid gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 md:grid-cols-[1fr_1fr_auto]">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as any)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
        >
          <option value="situation_photo">Situatiefoto</option>
          <option value="other">Overig bestand</option>
        </select>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titel (bijv. Meterkast voorzijde)"
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
        />
        <label className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-[#0b2a4a] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0e3463]">
          {uploadMut.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Upload className="size-3.5" />
          )}
          Uploaden
          <input
            ref={fileRef}
            type="file"
            accept={category === "situation_photo" ? "image/*" : undefined}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadMut.mutate(f);
            }}
          />
        </label>
      </div>

      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm">
            <p className="font-semibold text-emerald-900">
              Offerte-dossier samenstellen
            </p>
            <p className="text-xs text-emerald-800/80">
              Preview & volgorde: voorblad → offerte + voorwaarden → productbladen → werkbon → paklijst → facturen ({quoteNumber}).
            </p>
          </div>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setPreviewOpen(true)}
          >
            <Eye className="size-4 mr-1" />
            Preview & genereer
          </Button>
        </div>
      </div>

      {previewOpen && (
        <BundlePreviewDialog
          quoteId={quoteId}
          quoteNumber={quoteNumber}
          previewFn={previewFn}
          previewPdfFn={previewPdfFn}
          onClose={() => setPreviewOpen(false)}
          onGenerate={(sections) => bundleMut.mutate(sections)}
          isGenerating={bundleMut.isPending}
        />
      )}


      {q.isLoading ? (
        <div className="mt-4 text-sm text-slate-400">Documenten laden…</div>
      ) : (
        <div className="mt-4 space-y-4">
          <DocList
            title="Situatiefoto's"
            icon={<ImageIcon className="size-4 text-emerald-600" />}
            items={photos}
            onOpen={(id) => openMut.mutate(id)}
            onDelete={(id) => deleteMut.mutate(id)}
          />
          <DocList
            title="Dossierbundels"
            icon={<FolderOpen className="size-4 text-[#0b2a4a]" />}
            items={bundles}
            onOpen={(id) => openMut.mutate(id)}
            onDelete={(id) => deleteMut.mutate(id)}
          />
          <DocList
            title="Overige documenten"
            icon={<FileText className="size-4 text-slate-500" />}
            items={others}
            onOpen={(id) => openMut.mutate(id)}
            onDelete={(id) => deleteMut.mutate(id)}
          />
          {documents.length === 0 && (
            <p className="text-sm text-slate-500">
              Nog geen documenten. Upload situatiefoto's of genereer het dossier.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DocList({
  title,
  icon,
  items,
  onOpen,
  onDelete,
}: {
  title: string;
  icon: React.ReactNode;
  items: any[];
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {icon} {title} · {items.length}
      </div>
      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {items.map((d) => (
          <li key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-slate-800">{d.title}</div>
              <div className="text-[11px] text-slate-500">
                {CATEGORY_LABELS[d.category] ?? d.category} ·{" "}
                {new Date(d.created_at).toLocaleDateString("nl-NL")}
                {d.size_bytes ? ` · ${Math.round(d.size_bytes / 1024)} kB` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpen(d.id)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download className="size-3" /> Open
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm("Document verwijderen?")) onDelete(d.id);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
            >
              <Trash2 className="size-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

type BundleSectionPreview = {
  kind: "quote_cover" | "quote_terms" | "product_sheet" | "werkbon" | "paklijst" | "invoice" | "photo";
  ref_id: string | null;
  label: string;
  subtitle?: string;
  removable: boolean;
  selected: boolean;
  warning?: string;
};

function BundlePreviewDialog({
  quoteId,
  quoteNumber,
  previewFn,
  previewPdfFn,
  onClose,
  onGenerate,
  isGenerating,
}: {
  quoteId: string;
  quoteNumber: string;
  previewFn: any;
  previewPdfFn: any;
  onClose: () => void;
  onGenerate: (sections: Array<{ kind: BundleSectionPreview["kind"]; ref_id: string | null }>) => void;
  isGenerating: boolean;
}) {
  const [sections, setSections] = useState<BundleSectionPreview[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const preview = useQuery({
    queryKey: ["bundle-preview", quoteId],
    queryFn: () => previewFn({ data: { quote_id: quoteId } }),
    staleTime: 0,
  });

  if (preview.data && sections === null) {
    setSections(preview.data.sections as BundleSectionPreview[]);
  }
  if (preview.error && !error) setError((preview.error as any)?.message ?? "Kon preview niet laden");

  const move = (idx: number, dir: -1 | 1) => {
    if (!sections) return;
    const next = [...sections];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    // niet-verplaatsbare secties moeten vooraan blijven
    if (!next[idx].removable && dir === 1 && !next[j].removable) return;
    if (!next[j].removable && dir === -1) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setSections(next);
  };
  const toggle = (idx: number) => {
    if (!sections) return;
    const next = [...sections];
    if (!next[idx].removable) return;
    next[idx] = { ...next[idx], selected: !next[idx].selected };
    setSections(next);
  };
  const remove = (idx: number) => {
    if (!sections) return;
    const s = sections[idx];
    if (!s.removable) return;
    setSections(sections.filter((_, i) => i !== idx));
  };

  const submit = () => {
    if (!sections) return;
    const payload = sections
      .filter((s) => s.selected)
      .map((s) => ({ kind: s.kind, ref_id: s.ref_id ?? null }));
    if (payload.length === 0) {
      toast.error("Selecteer minimaal één sectie");
      return;
    }
    onGenerate(payload);
  };

  const downloadPreview = async () => {
    if (!sections) return;
    const payload = sections
      .filter((s) => s.selected)
      .map((s) => ({ kind: s.kind, ref_id: s.ref_id ?? null }));
    if (payload.length === 0) {
      toast.error("Selecteer minimaal één sectie");
      return;
    }
    setDownloading(true);
    try {
      const res = await previewPdfFn({ data: { quote_id: quoteId, sections: payload } });
      const bin = atob(res.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename ?? `preview-dossier-${quoteNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Preview gedownload — nog niet opgeslagen in klantdossier");
    } catch (e: any) {
      toast.error(e.message ?? "Preview downloaden mislukt");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div>
            <h3 className="font-semibold text-slate-900">Preview & volgorde dossier</h3>
            <p className="text-xs text-slate-500">Offerte {quoteNumber} — pas volgorde en selectie aan voor je genereert.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
            <X className="size-4" />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">
          {preview.isLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="size-4 animate-spin" /> Preview laden…
            </div>
          )}
          {error && <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
          {sections && (
            <ol className="space-y-2">
              {sections.map((s, idx) => (
                <li
                  key={`${s.kind}:${s.ref_id ?? idx}`}
                  className={`flex items-start gap-3 rounded-xl border p-3 ${
                    s.selected ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/40 opacity-60"
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                      title="Omhoog"
                    >
                      <ChevronUp className="size-3.5" />
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === sections.length - 1}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                      title="Omlaag"
                    >
                      <ChevronDown className="size-3.5" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {idx + 1}.
                      </span>
                      <span className="font-medium text-slate-900">{s.label}</span>
                      {!s.removable && (
                        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                          Verplicht
                        </span>
                      )}
                    </div>
                    {s.subtitle && <p className="text-xs text-slate-500">{s.subtitle}</p>}
                    {s.warning && (
                      <p className="mt-1 text-[11px] text-amber-700">⚠ {s.warning}</p>
                    )}
                  </div>
                  {s.removable && (
                    <div className="flex items-center gap-2">
                      <label className="inline-flex items-center gap-1 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={s.selected}
                          onChange={() => toggle(idx)}
                        />
                        Meesturen
                      </label>
                      <button
                        onClick={() => remove(idx)}
                        title="Verwijderen uit bundel"
                        className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
          {sections && preview.data && (
            <p className="mt-3 text-[11px] text-slate-500">
              Product­bladen zonder gekoppelde PDF krijgen automatisch een tekstpagina op basis van de productgegevens.
              Later kun je per product een PDF-specificatieblad koppelen via het productbeheer.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <Button variant="outline" onClick={onClose} disabled={isGenerating || downloading}>
            Annuleren
          </Button>
          <Button
            variant="outline"
            onClick={downloadPreview}
            disabled={!sections || downloading || isGenerating}
            title="Download een PDF-preview zonder deze op te slaan in het klantdossier"
          >
            {downloading ? (
              <Loader2 className="size-4 mr-1 animate-spin" />
            ) : (
              <Download className="size-4 mr-1" />
            )}
            Preview downloaden
          </Button>
          <Button
            onClick={submit}
            disabled={isGenerating || !sections}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isGenerating ? (
              <Loader2 className="size-4 mr-1 animate-spin" />
            ) : (
              <FileText className="size-4 mr-1" />
            )}
            Genereer dossier
          </Button>
        </div>
      </div>
    </div>
  );
}
